// @flow weak

import localforage from 'localforage';
import MobileDetect from 'mobile-detect';
import * as R from 'ramda';

import { HttpService } from '~/services';
import {
  STORAGE_KEY_APP_NAVIGATION_VISIBILITY,
  STORAGE_KEY_LOCALE,
  STORAGE_KEY_THEME,
} from '~/helpers';
import { SETTINGS } from '~/gql';

const getAcceptLanguageFromLocale = R.pipe(
  R.prop('locale'),
  R.cond([
    [R.equals('en'), R.always('en-US')],
    [R.equals('zh'), R.always('zh-CN')],
    [R.T, R.identity],
  ]),
);

const mobileDevice = new MobileDetect(window.navigator.userAgent);
const isMobileDevice = !!(mobileDevice.mobile() || mobileDevice.tablet());

export const resolvers = {
  Query: {
    settings: async (
      _, // eslint-disable-line no-unused-vars
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<Settings> => {
      let locale = await localforage.getItem(STORAGE_KEY_LOCALE);
      let theme = await localforage.getItem(STORAGE_KEY_THEME);
      let isNavigationVisible =
        (await localforage.getItem(STORAGE_KEY_APP_NAVIGATION_VISIBILITY)) ||
        isMobileDevice
          ? true
          : false;

      const settings = {
        isNavigationVisible,
        locale: locale || 'en',
        version: process.env.REACT_APP_VERSION || '0.0.0',
        theme: theme || 'defaultTheme',
        __typename: 'Settings',
      };

      // Update 'Accept-Language' header
      HttpService.setAcceptLanguage(getAcceptLanguageFromLocale(settings));

      cache.writeData({ data: { settings } });

      return settings;
    },
  },
  Mutation: {
    changeLocaleMutation: async (
      _, // eslint-disable-line no-unused-vars
      { locale }: LocaleInputMutation,
      { cache }: ApolloCache,
    ): Promise<MessagePayload> => {
      const { settings }: SettingsQuery = cache.readQuery({ query: SETTINGS });

      await Promise.all([
        localforage.setItem(STORAGE_KEY_LOCALE, locale),
        cache.reset(),
      ]);

      HttpService.setAcceptLanguage(getAcceptLanguageFromLocale({ locale }));

      cache.writeData({
        data: {
          settings: { ...settings, locale },
        },
      });

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    },
    changeThemeMutation: async (
      _, // eslint-disable-line no-unused-vars
      { theme }: { theme: string },
      { cache }: ApolloCache,
    ): Promise<MessagePayload> => {
      await localforage.setItem(STORAGE_KEY_THEME, theme);

      const { settings }: SettingsQuery = cache.readQuery({ query: SETTINGS });

      cache.writeData({
        data: {
          settings: {
            ...settings,
            theme,
          },
        },
      });

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    },
    setNavigationVisibilityMutation: async (
      _, // eslint-disable-line no-unused-vars
      { isVisible }: { isVisible: boolean },
      { cache }: ApolloCache,
    ): Promise<MessagePayload> => {
      await localforage.setItem(
        STORAGE_KEY_APP_NAVIGATION_VISIBILITY,
        isVisible,
      );

      const { settings }: SettingsQuery = cache.readQuery({ query: SETTINGS });

      cache.writeData({
        data: {
          settings: {
            ...settings,
            isNavigationVisible: isVisible,
          },
        },
      });

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    },
  },
};
